import { useCallback, useState } from 'react';
import cheerio from 'cheerio';
import { Video } from '../helpers/types';
import { getVideos } from '../helpers/videos';
import { getFriends } from '../helpers/friends';
import { getUsername } from '../helpers/users';
import { log } from '../helpers/supabase/log';

export type RunSearchOptions = {
  /** When true, keep existing crawled rows and concatenate the new batch (dedup still applies). Defaults to preserveResults hook prop. */
  append?: boolean;
  /** Overrides how many listing pages this run fetches (`start … start + pages - 1`). Defaults to hook `amount`. */
  pages?: number;
  /** Fires once after listings are merged successfully (same path as `logSearch`; not invoked on validation/404 exits). */
  onBatchComplete?: (info: { offset: number; pageCount: number; append: boolean }) => void;
};

interface UseSearchLogicProps {
  mode: string;
  type: string;
  id: string;
  friendId: string;
  primaryTag: string;
  category: string;
  minDuration: number;
  /** `null` = unlimited (slider max). */
  scrapeMinDuration?: number;
  logDuration?: number;
  quick: boolean;
  preserveResults: boolean;
  rawVideos: Video[];
  includeTags: string[];
  advanced: boolean;
  amount: number;
  setLoading: (loading: boolean) => void;
  setProgressCount: (count: number) => void;
  setRawVideos: (videos: Video[]) => void;
  setFinished: (finished: boolean) => void;
  setErrorMessage: (message: string) => void;
  setPageLimit: (limit: number) => void;
  setAmount: (amount: number) => void;
  setSourceExists: (exists: boolean) => void;
  setUsername: (username: string) => void;
  setFriends: (friends: any[]) => void;
  setSearchObject: (obj: any) => void;
  executeScroll: () => void;
}

export const useSearchLogic = ({
  mode,
  type,
  id,
  friendId,
  primaryTag,
  category,
  minDuration,
  scrapeMinDuration,
  logDuration,
  quick,
  preserveResults,
  rawVideos,
  includeTags,
  advanced,
  amount,
  setLoading,
  setProgressCount,
  setRawVideos,
  setFinished,
  setErrorMessage,
  setPageLimit,
  setAmount,
  setSourceExists,
  setUsername,
  setFriends,
  setSearchObject,
  executeScroll,
}: UseSearchLogicProps) => {
  const [pageLimitLoading, setPageLimitLoading] = useState(false);
  const effectiveScrapeMinDuration = scrapeMinDuration ?? minDuration;
  const effectiveLogDuration = logDuration ?? minDuration;

  const getUrl = useCallback((page: number): string => {
    const idTrim = id?.trim() ?? '';
    const friendTrim = friendId?.trim() ?? '';
    const baseUrl: { [key: string]: string } = {
      newest: `/${type}/${page}/`,
      user:
        idTrim && type ? `/members/${idTrim}/${type}_videos/${page}/` : '',
      friend:
        friendTrim && type ? `/members/${friendTrim}/${type}_videos/${page}/` : '',
      tags: `/tags/${primaryTag}/${type}-males/${page}/`,
      category: `/categories/${category}/${type}/${page}/`,
      extreme: `/${type}/${page}/?q=${primaryTag}`,
    };

    if (mode === 'category' && type === 'latest') {
      return `/categories/${category}/${page}/`;
    }

    return baseUrl[mode] || '';
  }, [mode, type, id, friendId, primaryTag, category]);

  const getPageLimitUrl = useCallback((): string => {
    const idTrim = id?.trim() ?? '';
    const friendTrim = friendId?.trim() ?? '';
    const baseUrl: { [key: string]: string } = {
      newest: `/${type}/`,
      user: idTrim && type ? `/members/${idTrim}/${type}_videos/` : '',
      friend: friendTrim && type ? `/members/${friendTrim}/${type}_videos/` : '',
      tags: `/tags/${primaryTag}/popular-males/`,
      category: `/categories/${category}/`,
      extreme: `/${type}/1/?q=${primaryTag}`,
    };

    return baseUrl[mode] || '';
  }, [mode, type, id, friendId, primaryTag, category]);

  const getSourceUrl = useCallback(() => {
    const idTrim = id?.trim() ?? '';
    const friendTrim = friendId?.trim() ?? '';
    const baseUrl: { [key: string]: string } = {
      newest: `/${type}/`,
      user: idTrim ? `/members/${idTrim}/` : '',
      friend: friendTrim ? `/members/${friendTrim}/` : '',
      tags: `/tags/${primaryTag}/`,
      category: `/categories/${category}/`,
    };

    return baseUrl[mode] || '';
  }, [mode, type, id, friendId, primaryTag, category]);

  const urlExists = async (url: string) => {
    const response = await fetch(url);
    return response.status !== 404;
  };

  const checkSourceExists = async () => {
    const url = getSourceUrl();
    const exists = await urlExists(url);
    setSourceExists(exists);
    return exists;
  };

  const getPageLimit = useCallback(async () => {
    const url = getPageLimitUrl();
    if (
      !url.trim() ||
      (mode === 'extreme' && (!type || !primaryTag)) ||
      (mode === 'newest' && !type) ||
      (mode === 'user' && (!(id?.trim?.() ?? '') || !type)) ||
      (mode === 'friend' && (!(friendId?.trim?.() ?? '') || !type))
    ) {
      setPageLimitLoading(false);
      return;
    }

    setPageLimitLoading(true);
    try {
      const response = await fetch(url);

      if (response) {
        if (response.status === 404) {
          return;
        }

        const body = await response.text();
        const $ = cheerio.load(body);

        const lastPage =
          parseInt(
            $('li.pagination-last a').text() || $('.pagination-list li:nth-last-child(2) a').text(),
          ) || 1;

        setPageLimit(lastPage);
        setAmount(lastPage < 100 ? lastPage : 100);
      }
    } finally {
      setPageLimitLoading(false);
    }
  }, [getPageLimitUrl, mode, type, primaryTag, id, friendId, setPageLimit, setAmount]);

  const updateUsername = useCallback(async () => {
    const username = await getUsername(id);

    if (username) {
      setUsername(username);
    } else {
      setErrorMessage(`User ${id} does not exist.`);
      setUsername('');
    }
  }, [id, setUsername, setErrorMessage]);

  const getFriendsById = async () => {
    setLoading(true);
    const f = await getFriends(id, setAmount, setProgressCount);

    if (!f.success) {
      setErrorMessage('User not found');
    } else if (f.friends.length === 0) {
      setErrorMessage('No friends found');
    }

    setFriends(f.friends);
    setLoading(false);
    setFinished(true);
    localStorage.setItem('uid', id);
  };

  const logSearch = async (pageAmountOverride?: number) => {
    const pageAmount =
      typeof pageAmountOverride === 'number' && Number.isFinite(pageAmountOverride)
        ? pageAmountOverride
        : amount;
    const s = await log({
      mode,
      type,
      advanced,
      tags: includeTags,
      pageAmount,
      quick,
      duration: effectiveLogDuration,
      primaryTag,
      category,
      userId: id,
      friendId,
      resultCount: 0, // Will be updated after filtering
      visitorId: localStorage.getItem('visitorId') || '',
      visitorName: localStorage.getItem('visitorName') || '',
    });
    setSearchObject(s);
  };

  const run = async (offset: number, options?: RunSearchOptions) => {
    setLoading(true);
    setProgressCount(0);

    const shouldAppend = options?.append ?? preserveResults;

    let pageCount =
      typeof options?.pages === 'number' && Number.isFinite(options.pages)
        ? Math.max(1, Math.floor(options.pages))
        : Math.max(1, Math.floor(Number(amount)) || 1);

    if (!shouldAppend) {
      setRawVideos([]);
    }
    const promises = [];
    let progress = 0;
    let tempPageLimit = 0;

    const urlFirstPage = getUrl(offset);
    if (!urlFirstPage.trim()) {
      setFinished(true);
      setErrorMessage(
        mode === 'user'
          ? 'Enter a member ID before running search.'
          : mode === 'friend'
            ? 'Select a friend before running search.'
            : 'Incomplete search inputs.',
      );
      setLoading(false);
      return;
    }

    const firstPageExists = await urlExists(urlFirstPage);

    if (!firstPageExists) {
      setFinished(true);
      setErrorMessage(`Page ${offset} does for ${mode} not exist.`);
      setLoading(false);
      return;
    }

    for (let i = offset; i <= offset - 1 + pageCount; i++) {
      const url = getUrl(i);
      const currentPage = i;
      promises.push(
        getVideos({
          url,
          minDuration: effectiveScrapeMinDuration,
          quick,
          page: currentPage,
        // eslint-disable-next-line no-loop-func -- callback runs when each page settles; shared progress/tempPageLimit (see Search/index.tsx)
        }).then((s) => {
          // @ts-ignore
          if (s && s.error === 404) {
            if (tempPageLimit === 0 || tempPageLimit > currentPage) {
              tempPageLimit = currentPage - 1;
              setPageLimit(tempPageLimit);
            }
          }
          progress++;
          setProgressCount(progress);

          return {
            videos: s,
            page: currentPage,
            // @ts-ignore
            hasError: s && s.error === 404,
          };
        }),
      );
    }

    try {
      const results = await Promise.all(promises);

      // Extract videos
      const videos = results.flatMap(r => r.videos).filter(
        // @ts-ignore
        (value, index, self) => index === self.findIndex((v) => v.url === value.url),
      );

      const newRawVideos = shouldAppend
        ? [...rawVideos, ...videos] as Video[]
        : videos as Video[];

      setRawVideos(newRawVideos);
      setFinished(true);
      executeScroll();
      options?.onBatchComplete?.({
        offset,
        pageCount,
        append: shouldAppend,
      });
      logSearch(pageCount);
    } catch (error) {
      console.log('Error: ' + error);
    }

    setLoading(false);
  };

  return {
    getUrl,
    getPageLimitUrl,
    getSourceUrl,
    urlExists,
    checkSourceExists,
    getPageLimit,
    pageLimitLoading,
    updateUsername,
    getFriendsById,
    logSearch,
    run,
  };
};
